import { useMutation, useQueryClient } from '@tanstack/react-query';

import { IMPORT_STATE } from '@/entities/(integration)/integrations';

import type { HelpscoutInfo } from '../model';
import { createHelpscoutInfo } from './create-helpscout-info';
import { deleteHelpscoutInfo } from './delete-helpscout-info';
import { helpscoutQueryKeys } from './helpscout.queries';
import { importHelpscoutFeedback } from './import-helpscout-feedback';
import { updateHelpscoutMailboxes } from './update-helpscout-mailboxes';

/**
 * Helpscout credential 생성 mutation
 */
export const useCreateHelpscoutMutation = (workspaceAlias: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: { client_id: string; client_secret: string }) =>
      createHelpscoutInfo({
        workspace_id: workspaceAlias,
        ...params,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: helpscoutQueryKeys.all(workspaceAlias),
      });
    },
  });
};

/**
 * Helpscout 연동 해제 mutation
 */
export const useDeleteHelpscoutMutation = (workspaceAlias: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => deleteHelpscoutInfo({ workspace_id: workspaceAlias }),
    onSuccess: () => {
      queryClient.setQueryData(helpscoutQueryKeys.all(workspaceAlias), null);
    },
  });
};

/**
 * Helpscout 피드백 일회성 가져오기 mutation
 */
export const useImportHelpscoutFeedbackMutation = (workspaceAlias: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ dateRange }: { dateRange: NullableDateRangeT }) =>
      importHelpscoutFeedback({
        workspace_id: workspaceAlias,
        start_date: dateRange.start_date || '',
        end_date: dateRange.end_date || '',
      }),
    onSuccess: () => {
      queryClient.setQueryData(
        helpscoutQueryKeys.all(workspaceAlias),
        (old: HelpscoutInfo | null | undefined) => {
          if (!old) return old;
          return {
            ...old,
            import_state: IMPORT_STATE.IMPORTING,
          };
        },
      );
    },
  });
};

/**
 * Helpscout mailbox 저장 mutation
 */
export const useUpdateHelpscoutMailboxesMutation = (workspaceAlias: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ mailbox_ids }: { mailbox_ids: string[] }) =>
      updateHelpscoutMailboxes({
        workspace_id: workspaceAlias,
        mailbox_ids: mailbox_ids.map(Number),
      }),
    onSuccess: (workspace) => {
      queryClient.setQueryData(
        helpscoutQueryKeys.all(workspaceAlias),
        workspace,
      );
    },
  });
};
